'use client';

import { useEffect, useState } from 'react';
import { computeHash, hashLabelByVariant, type HashVariant } from '@/lib/browser-hash-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserHashGenerator({ variant }: { variant: HashVariant }) {
  const label = hashLabelByVariant[variant];
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    computeHash(variant, input)
      .then((hash) => {
        if (!cancelled) setOutput(hash);
      })
      .catch(() => {
        if (!cancelled) setOutput('');
      });
    return () => {
      cancelled = true;
    };
  }, [variant, input]);

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Hash copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{label}</span>
        <h2 className="tool-live-title">Generate a {label} hash</h2>
        <p className="tool-live-subtitle">
          Type or paste text and the {label} hash updates live in your browser using the Web Crypto
          API. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Text to hash</span>
            <textarea
              onChange={(event) => {
                setCopyMessage(null);
                setInput(event.target.value);
              }}
              placeholder="Enter text to hash"
              rows={6}
              value={input}
            />
          </label>
          <div className="tool-live-actions">
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy hash
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>{label} hash (hex)</span>
            <textarea readOnly rows={4} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Hashing runs locally with the Web Crypto API. Your text is never uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
